/**
 * The local store — a thin promise wrapper over IndexedDB.
 *
 * Deliberately dumb. Every decision worth testing lives in outboxRules.js,
 * which runs under `node --test`; this file is the part that cannot be tested
 * without a browser, so it is kept mechanical and free of judgement.
 *
 * No `idb` dependency. The wrapper below is about ninety lines and the project
 * ships enough packages already.
 */

const DB_NAME = "csa";

/**
 * Schema version.
 *
 * The contract (design §9) is explicit: "a tablet that skipped three app
 * releases must still open a half-finished audit rather than lose it". So
 * migrations are a list applied in order, never a drop-and-recreate. A device
 * on v1 opening a v4 build runs steps 2, 3 and 4 in sequence.
 *
 * To change the schema: append a step, raise DB_VERSION, and never edit an
 * existing step — a device that already ran it will not run it again.
 */
const DB_VERSION = 1;

const MIGRATIONS = [
  // 1 — the initial store set.
  (db) => {
    db.createObjectStore("meta", { keyPath: "key" });

    // Questions by framework version. Roughly 200 KB for the current 163.
    db.createObjectStore("framework", { keyPath: "version" });

    // Audit header, client, scope exclusions, last known roll-up.
    db.createObjectStore("audits", { keyPath: "audit_id" });

    const answers = db.createObjectStore("answers", { keyPath: ["audit_id", "question_code"] });
    answers.createIndex("by_audit", "audit_id", { unique: false });

    const notes = db.createObjectStore("notes", { keyPath: ["audit_id", "local_id"] });
    notes.createIndex("by_audit", "audit_id", { unique: false });

    const extras = db.createObjectStore("extras", { keyPath: ["audit_id", "local_id"] });
    extras.createIndex("by_audit", "audit_id", { unique: false });

    /*
     * Reserved for the camera roll. The design warns that retrofitting photos
     * means reopening the outbox, so the store and its indexes exist from the
     * first version even though nothing writes to them yet — a device
     * provisioned today will not need a migration when photos ship.
     */
    const photos = db.createObjectStore("photos", { keyPath: ["audit_id", "local_id"] });
    photos.createIndex("by_audit", "audit_id", { unique: false });
    photos.createIndex("by_state", "upload_state", { unique: false });

    // Keyed by the idempotency key, so queueing the same write twice is a
    // no-op at the storage layer as well as at the server.
    const outbox = db.createObjectStore("outbox", { keyPath: "key" });
    outbox.createIndex("by_audit", "audit_id", { unique: false });
    outbox.createIndex("by_seq", ["audit_id", "seq"], { unique: false });
  },
];

let dbPromise = null;

/** Is IndexedDB usable at all? Private browsing and old WebViews say no. */
export function isSupported() {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch (e) {
    return false;
  }
}

export function open() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (!isSupported()) {
      reject(new Error("This browser cannot store an audit offline"));
      return;
    }

    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = req.result;
      const from = event.oldVersion || 0;
      // Run every step the device has not seen, in order.
      for (let v = from; v < DB_VERSION; v++) {
        MIGRATIONS[v](db, req.transaction);
      }
    };

    req.onsuccess = () => {
      const db = req.result;
      /*
       * Another tab running a newer build is trying to upgrade. Close so it
       * can, rather than deadlocking both — this tab reopens on next use.
       */
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };

    req.onerror = () => reject(req.error || new Error("Could not open the local store"));
    req.onblocked = () => reject(new Error("Another tab is holding the local store open"));
  });

  return dbPromise;
}

/* ------------------------------------------------------------------ */
/* primitives                                                          */
/* ------------------------------------------------------------------ */

function run(storeNames, mode, fn) {
  return open().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeNames, mode);
        let result;
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error("Local write aborted"));
        try {
          result = fn(tx);
        } catch (err) {
          try { tx.abort(); } catch (e) { /* already gone */ }
          reject(err);
        }
      })
  );
}

const asPromise = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export function get(store, key) {
  return open().then((db) =>
    asPromise(db.transaction(store, "readonly").objectStore(store).get(key))
  );
}

export function getAll(store, indexName, query) {
  return open().then((db) => {
    const os = db.transaction(store, "readonly").objectStore(store);
    const src = indexName ? os.index(indexName) : os;
    return asPromise(src.getAll(query === undefined ? null : query));
  });
}

export function put(store, value) {
  return run(store, "readwrite", (tx) => {
    tx.objectStore(store).put(value);
    return value;
  });
}

export function putMany(store, values) {
  return run(store, "readwrite", (tx) => {
    const os = tx.objectStore(store);
    values.forEach((v) => os.put(v));
    return values.length;
  });
}

export function del(store, key) {
  return run(store, "readwrite", (tx) => {
    tx.objectStore(store).delete(key);
  });
}

export function delMany(store, keys) {
  return run(store, "readwrite", (tx) => {
    const os = tx.objectStore(store);
    keys.forEach((k) => os.delete(k));
    return keys.length;
  });
}

/** Write across several stores in ONE transaction, so a half-write cannot happen. */
export function transact(storeNames, fn) {
  return run(storeNames, "readwrite", fn);
}

/* ------------------------------------------------------------------ */
/* meta                                                                */
/* ------------------------------------------------------------------ */

export function getMeta(key, fallback) {
  return get("meta", key).then((row) => (row === undefined ? fallback : row.value));
}

export function setMeta(key, value) {
  return put("meta", { key, value });
}

/**
 * The sequence counter, per audit.
 *
 * Monotonic and never reused — that is what makes an idempotency key stable
 * across retries. Read-modify-write inside one transaction so two rapid taps
 * cannot be handed the same number.
 */
export function nextSeq(auditId) {
  const key = "seq:" + auditId;
  return run("meta", "readwrite", (tx) => {
    const os = tx.objectStore("meta");
    const req = os.get(key);
    const out = { value: null };
    req.onsuccess = () => {
      const n = (req.result ? req.result.value : 0) + 1;
      out.value = n;
      os.put({ key, value: n });
    };
    return out;
  }).then((out) => out.value);
}

/**
 * This device's id, minted once and kept.
 *
 * It identifies the tablet holding an audit (§9, Device claim), so it must
 * survive reloads — but it is not an identity claim and grants nothing.
 */
export function deviceId() {
  return getMeta("device_id", null).then((existing) => {
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : "dev-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    return setMeta("device_id", id).then(() => id);
  });
}

/* ------------------------------------------------------------------ */
/* housekeeping                                                        */
/* ------------------------------------------------------------------ */

/**
 * How much room is left.
 *
 * The design is specific about why: "discovering the quota is full at photo
 * 200, on site, is unrecoverable." Checked at provisioning and during capture.
 */
export function estimateStorage() {
  if (typeof navigator === "undefined" || !navigator.storage || !navigator.storage.estimate) {
    return Promise.resolve(null);
  }
  return navigator.storage
    .estimate()
    .then((e) => ({
      usage: e.usage || 0,
      quota: e.quota || 0,
      pct: e.quota ? Math.round(((e.usage || 0) / e.quota) * 100) : 0,
    }))
    .catch(() => null);
}

/**
 * Ask the browser not to evict this origin's data under storage pressure.
 * Advisory: some browsers grant it silently, some prompt, some ignore it.
 */
export function requestPersistence() {
  if (typeof navigator === "undefined" || !navigator.storage || !navigator.storage.persist) {
    return Promise.resolve(false);
  }
  return navigator.storage.persist().catch(() => false);
}

/**
 * Forget one audit's local copy.
 *
 * Refuses while anything is still queued — dropping the store would throw away
 * work that never reached the server, which is the one thing this whole layer
 * exists to prevent.
 */
export function forgetAudit(auditId) {
  return getAll("outbox", "by_audit", auditId).then((pending) => {
    if (pending.length) {
      const err = new Error(
        `${pending.length} change${pending.length === 1 ? "" : "s"} have not synced yet`
      );
      err.pending = pending.length;
      throw err;
    }

    return Promise.all([
      getAll("answers", "by_audit", auditId),
      getAll("notes", "by_audit", auditId),
      getAll("extras", "by_audit", auditId),
      getAll("photos", "by_audit", auditId),
    ]).then(([answers, notes, extras, photos]) =>
      transact(["audits", "answers", "notes", "extras", "photos"], (tx) => {
        tx.objectStore("audits").delete(auditId);
        answers.forEach((a) => tx.objectStore("answers").delete([a.audit_id, a.question_code]));
        notes.forEach((n) => tx.objectStore("notes").delete([n.audit_id, n.local_id]));
        extras.forEach((x) => tx.objectStore("extras").delete([x.audit_id, x.local_id]));
        photos.forEach((p) => tx.objectStore("photos").delete([p.audit_id, p.local_id]));
      })
    );
  });
}

export const DB = { name: DB_NAME, version: DB_VERSION };
